import {
  Box,
  Card,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Grow,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popper,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import clsx from 'clsx';
import React, {
  CSSProperties,
  FC,
  MutableRefObject,
  ReactNode,
  useRef,
  useMemo,
} from 'react';
import { AutoSizer, Table as RVTable } from 'react-virtualized';
import { useStyles } from './styles';
import { SubdirectoryArrowRight } from '@mui/icons-material';

export enum AutocompleteMode {
  BASIC,
  MULTIPLE,
}

export interface AutocompleteItem {
  additionnals?: Record<string, any>;
  disabled?: boolean;
  id: string;
  label: ReactNode;
  level?: number;
  loading?: boolean;
  secondaryLabel?: ReactNode;
  selected?: boolean;
  color?: string;
  isInactive?: boolean;
}

interface AutocompleteListProps {
  anchorRef: MutableRefObject<any>;
  disabled?: boolean;
  error?: Error;
  list?: AutocompleteItem[];
  listHeader?: ((onClose?: () => void) => ReactNode) | ReactNode;
  loading?: boolean;
  mode?: AutocompleteMode;
  onClose?: () => void;
  onSelect?: (item: AutocompleteItem) => void;
  open: boolean;
  placeholder?: ((onClose?: () => void) => ReactNode) | ReactNode;
  scrollToItem?: number;
  showLevelIcons?: boolean;
}

interface RenderListItemProps
  extends Pick<
    AutocompleteListProps,
    'mode' | 'onSelect' | 'showLevelIcons' | 'disabled'
  > {
  item: AutocompleteItem;
  index: number;
  isBusy: boolean;
  style?: CSSProperties;
}

const RenderListItem: FC<RenderListItemProps> = ({
  item,
  index,
  disabled,
  mode,
  onSelect,
  showLevelIcons,
  isBusy,
  style,
}) => {
  const classes = useStyles({ index, level: item.level });

  const isListItemDisabled =
    disabled || (!item.loading && (item.disabled || item.selected));

  const itemLabel = item.isInactive ? `${item.label} (désactivé)` : item.label;
  const disabledItemStyle = item.isInactive
    ? {
        fontStyle: 'italic',
        color: 'gray',
      }
    : {};

  return (
    <ListItem
      button
      data-testid="auto-complete-list-item"
      disabled={isListItemDisabled}
      classes={{ root: classes.listItem }}
      className={clsx(
        isListItemDisabled && classes.listItemDisabled,
        (item.selected || item.loading || item.secondaryLabel) &&
          classes.listItemSecondaryLabel,
        (item.selected || item.loading) && classes.listItemSelected,
      )}
      onMouseDown={() => {
        if (!item.disabled && !item.selected) {
          onSelect?.(item);
        }
      }}
      selected={item.selected}
      style={{
        ...style,
        pointerEvents: isBusy ? 'none' : 'auto',
      }}
    >
      {showLevelIcons && !!item.level && (
        <Box className={classes.boxMarginLeft}>
          <SubdirectoryArrowRight color="disabled" />
        </Box>
      )}
      <ListItemText
        classes={{
          primary: clsx(
            classes.label,
            showLevelIcons && !item.level && classes.labelWeight,
          ),
        }}
        primary={
          item.color ? (
            <Chip
              label={itemLabel}
              color="primary"
              sx={{
                backgroundColor: item.color,
                ...disabledItemStyle,
              }}
            />
          ) : (
            <Box sx={disabledItemStyle}>{itemLabel}</Box>
          )
        }
      />

      <div className={classes.wrapperListItemRight}>
        {mode === AutocompleteMode.MULTIPLE &&
          (item.loading ? (
            <ListItemIcon className={classes.listItemIcon}>
              <CircularProgress size={15} />
            </ListItemIcon>
          ) : (
            item.selected && (
              <ListItemIcon className={classes.listItemIcon}>
                <CheckCircleIcon fontSize="small" />
              </ListItemIcon>
            )
          ))}

        {item.secondaryLabel && (
          <ListItemText
            classes={{
              root: classes.secondaryLabel,
              secondary: classes.label,
            }}
            disableTypography
            primary={item.secondaryLabel}
          />
        )}
      </div>
    </ListItem>
  );
};

export const AutocompleteList: FC<AutocompleteListProps> = ({
  anchorRef,
  disabled,
  error,
  list = [],
  listHeader,
  loading,
  mode,
  onClose,
  onSelect,
  open,
  placeholder = 'Aucun résultat',
  scrollToItem,
  showLevelIcons,
}) => {
  const classes = useStyles({});
  const listHeaderRef = useRef<HTMLLIElement>(null);
  const isBusy = useMemo(
    () => Boolean(list.find(({ loading: itemListLoading }) => itemListLoading)),
    [list],
  );
  const isBigList = list.length > 7 - (listHeader ? 1 : 0);

  const placeholderEl = useMemo(
    () =>
      typeof placeholder === 'function' ? placeholder(onClose) : placeholder,
    [onClose, placeholder],
  );

  const listHeaderEl = useMemo(
    () => (typeof listHeader === 'function' ? listHeader(onClose) : listHeader),
    [onClose, listHeader],
  );

  return (
    <Popper
      anchorEl={anchorRef.current}
      className={classes.popper}
      open={open}
      style={{ width: anchorRef.current?.clientWidth }}
      transition
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === 'bottom' ? 'center top' : 'center bottom',
          }}
        >
          <div>
            <ClickAwayListener
              mouseEvent="onMouseDown"
              onClickAway={(e) => {
                if (!anchorRef.current.contains(e.target)) {
                  onClose?.();
                }
              }}
              touchEvent="onTouchStart"
            >
              <Card
                classes={{ root: classes.card }}
                raised
                {...(placement !== 'bottom' && {
                  style: {
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                  },
                })}
              >
                {loading || error || !list.length ? (
                  <div className={classes.listMessage}>
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Typography
                        variant="body2"
                        color={error ? 'error' : undefined}
                      >
                        {error
                          ? error.message || 'Une erreur est survenue'
                          : placeholderEl}
                      </Typography>
                    )}
                  </div>
                ) : (
                  <List
                    className={clsx(isBigList && classes.bigList)}
                    data-testid="auto-complete-list"
                    dense
                  >
                    {isBigList ? (
                      <AutoSizer>
                        {({ width, height }) => (
                          <RVTable
                            disableHeader={!listHeader}
                            headerHeight={
                              listHeaderRef?.current?.clientHeight || 50
                            }
                            headerRowRenderer={() => (
                              <ListSubheader
                                classes={{ root: classes.listHeader }}
                                ref={listHeaderRef}
                                style={{ width }}
                              >
                                {listHeaderEl}
                              </ListSubheader>
                            )}
                            height={height}
                            gridStyle={{ paddingTop: listHeader ? 8 : 0 }}
                            rowHeight={36}
                            rowStyle={{ paddingRight: 16 * 2 }}
                            rowCount={list.length}
                            rowGetter={({ index }) => list[index]}
                            rowRenderer={({ key, index, style }) => (
                              <RenderListItem
                                key={key}
                                mode={mode}
                                onSelect={onSelect}
                                isBusy={isBusy}
                                showLevelIcons={showLevelIcons}
                                disabled={disabled}
                                item={list[index]}
                                index={index}
                                style={style}
                              />
                            )}
                            scrollToAlignment="auto"
                            scrollToIndex={scrollToItem}
                            style={{ outline: 0 }}
                            width={width}
                          />
                        )}
                      </AutoSizer>
                    ) : (
                      <>
                        {listHeader && (
                          <ListSubheader
                            classes={{ root: classes.listHeader }}
                            style={{ marginBottom: 8 }}
                          >
                            {listHeaderEl}
                          </ListSubheader>
                        )}

                        {list.map((item, i) => (
                          <RenderListItem
                            key={`autocomplete-item-${i}`}
                            mode={mode}
                            onSelect={onSelect}
                            isBusy={isBusy}
                            showLevelIcons={showLevelIcons}
                            disabled={disabled}
                            item={item}
                            index={i}
                          />
                        ))}
                      </>
                    )}
                  </List>
                )}
              </Card>
            </ClickAwayListener>
          </div>
        </Grow>
      )}
    </Popper>
  );
};
